/* actualizado para mapa con Leaflet */
/* global L */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { inspectorService } from '../services/inspectorService';
import { storageService } from '../services/storageService';
import { useAuth } from '../context/AuthContext';
import InspectorDashboardTab from './InspectorDashboardTab';
import InspectorPEMapModal from './InspectorPEMapModal';
import { InspectorLoadingOverlay } from '../components/InspectorLoadingOverlay';
import ImageViewerModal from '../components/ImageViewerModal';
import { formatInspectorError } from '../utils/errorUtils';
import { isSimulatorAllowed } from '../utils/envUtils';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { API_BASE_URL, getMediaBaseUrl } from '../config';
import { reportDeviceStatus } from '../services/deviceStatusService';
import { useDevModeGuard } from '../hooks/useDevModeGuard';

// Hook para manejar retroceso de historial en modales
const useBackHandler = (active, onBack, name) => {
    const isManual = useRef(false);

    useEffect(() => {
        if (!active) return;

        isManual.current = false;

        window.history.pushState({ modalName: name }, '');

        const handlePopState = (e) => {
            if (isManual.current) {
                isManual.current = false;
                return;
            }

            const currentState = e.state || {};
            if (currentState.modalName !== name) {
                onBack();
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [active, onBack, name]);

    const triggerManualBack = useCallback((...args) => {
        if (active) {
            isManual.current = true;
            window.history.back();
        }
        onBack(...args);
    }, [active, onBack]);

    return triggerManualBack;
};



const Toast = ({ show, message, type, onClose }) => {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(onClose, 3000);
            return () => clearTimeout(timer);
        }
    }, [show, onClose]);

    if (!show) return null;

    const colors = {
        success: '#28a745',
        error: '#dc3545',
        warning: '#ffc107',
        info: '#17a2b8'
    };

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: colors[type] || '#007bff',
            color: type === 'warning' ? '#000' : 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '300px'
        }}>
            {message}
        </div>
    );
};

/* --- Modal del mapa con Leaflet --- */
const MapModal = ({ unrealizable, onClose }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);

    useEffect(() => {
        if (!unrealizable || !mapRef.current) return;
        const lat = parseFloat(unrealizable.latitude);
        const lng = parseFloat(unrealizable.longitude);

        let isMounted = true;

        const renderMap = (inspectorLat = null, inspectorLng = null) => {
            if (!isMounted || !mapRef.current) return;

            if (!mapInstance.current) {
                mapInstance.current = L.map(mapRef.current).setView([lat, lng], 13);

                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; OpenStreetMap contributors'
                }).addTo(mapInstance.current);
                setTimeout(() => { if (mapInstance.current) mapInstance.current.invalidateSize(); }, 250);
            }

            // Limpiar marcadores previos
            mapInstance.current.eachLayer((layer) => {
                if (layer instanceof L.Marker || layer instanceof L.Polyline) {
                    mapInstance.current.removeLayer(layer);
                }
            });

            // Marcador incidencia (rojo)
            const incMarker = L.marker([lat, lng], {
                icon: L.icon({
                    iconUrl: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                    iconSize: [32, 32],
                    iconAnchor: [16, 32]
                })
            }).addTo(mapInstance.current).bindPopup("📍 Incidencia");

            if (inspectorLat !== null && inspectorLng !== null) {
                // Marcador inspector (azul)
                L.marker([inspectorLat, inspectorLng], {
                    icon: L.icon({
                        iconUrl: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                        iconSize: [32, 32],
                        iconAnchor: [16, 32]
                    })
                }).addTo(mapInstance.current).bindPopup("👷 Inspector");

                // Línea y distancia
                const line = L.polyline([[lat, lng], [inspectorLat, inspectorLng]], { color: 'green' })
                    .addTo(mapInstance.current);

                const distance = mapInstance.current.distance([lat, lng], [inspectorLat, inspectorLng]) / 1000;
                line.bindPopup(`Distancia: ${distance.toFixed(2)} km`).openPopup();

                mapInstance.current.fitBounds(line.getBounds(), { padding: [50, 50] });
            } else {
                incMarker.openPopup();
            }
        };

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => renderMap(pos.coords.latitude, pos.coords.longitude),
                (err) => {
                    console.warn("⚠️ No se pudo obtener ubicación del inspector para el mapa:", err);
                    renderMap(null, null);
                },
                { enableHighAccuracy: true, timeout: 6000 }
            );
        } else {
            renderMap(null, null);
        }

        return () => {
            isMounted = false;
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, [unrealizable]);

    return (
        <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.7)", display: "flex",
            justifyContent: "center", alignItems: "center", zIndex: 2000
        }}>
            <div style={{
                width: "90%", height: "80%", background: "white",
                borderRadius: "12px", overflow: "hidden", position: "relative"
            }}>
                <div ref={mapRef} style={{ width: "100%", height: "100%" }}></div>
                <button onClick={onClose} style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    zIndex: 1000,  // ← AGREGAR ESTO
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)"  // ← AGREGAR ESTO
                }}>
                    ✕ Cerrar
                </button>
                <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${unrealizable.latitude},${unrealizable.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        position: "absolute",
                        bottom: "10px",
                        right: "10px",
                        background: "#007bff",
                        color: "white",
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        textDecoration: "none",
                        fontWeight: "600",
                        zIndex: 1000,  // ← AGREGAR ESTO
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)"  // ← AGREGAR ESTO
                    }}
                >
                    🚶 Navegar con Google Maps
                </a>
            </div>
        </div>
    );
};

const UnrealizableDetailModal = ({ unrealizable, onClose, onApprove, onReject }) => {
    const [decisionData, setDecisionData] = useState({
        justification_duration: unrealizable.available_durations?.[0] || 30,
        inspector_comments: '',
        evidence_photos: []
    });
    const [previews, setPreviews] = useState([]);
    const [makingDecision, setMakingDecision] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationType, setConfirmationType] = useState(null); // 'approve' or 'reject'
    const [uploadProgress, setUploadProgress] = useState(0);
    const [expandedImage, setExpandedImage] = useState(null);
    const [currentPosition, setCurrentPosition] = useState(null);
    const [compressing, setCompressing] = useState(false);

    const handleCloseConfirmation = useBackHandler(showConfirmation, () => setShowConfirmation(false), 'showConfirmation');

    useEffect(() => {
        return () => {
            previews.forEach(url => URL.revokeObjectURL(url));
        };
    }, [previews]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setCurrentPosition({
                        latitude: pos.coords.latitude.toString(),
                        longitude: pos.coords.longitude.toString()
                    });
                },
                (err) => {
                    console.error("Error pre-fetching location:", err);
                },
                // Pre-fetch agresivo: acepta posición de hasta 2 min de antigüedad
                // para que el submit sea instantáneo si ya hay GPS disponible
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 120000 }
            );
        }
    }, []);

    // 🎯 Función para comprimir imagen con límite de peso de 750 KB (0.7 MB)
    const compressImage = (file, maxSizeMB = 0.7) => {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                // Liberar el Object URL inmediatamente para liberar RAM
                URL.revokeObjectURL(objectUrl);

                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Mantener aspecto reduciendo si es muy grande
                const maxWidth = 1200;
                const maxHeight = 1200;
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                const cleanup = () => {
                    canvas.width = 0;
                    canvas.height = 0;
                    img.onload = null;
                    img.onerror = null;
                    img.src = '';
                };

                // Compresión eficiente en máximo 2 pasos para ahorrar CPU y RAM
                const quality = 0.70; // 0.70 es excelente calidad visual y tamaño muy optimizado (~150KB)
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            cleanup();
                            reject(new Error('Error al generar blob de la imagen'));
                            return;
                        }
                        const sizeMB = blob.size / (1024 * 1024);
                        if (sizeMB > maxSizeMB) {
                            canvas.toBlob(
                                (secondBlob) => {
                                    cleanup();
                                    if (!secondBlob) {
                                        resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                                        return;
                                    }
                                    console.log(`📦 [Incidencia] Compresión (Paso 2): ${(file.size / 1024).toFixed(1)} KB -> ${(secondBlob.size / 1024).toFixed(1)} KB`);
                                    resolve(new File([secondBlob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                                },
                                'image/jpeg',
                                0.50
                            );
                        } else {
                            cleanup();
                            console.log(`📦 [Incidencia] Compresión (Paso 1): ${(file.size / 1024).toFixed(1)} KB -> ${(blob.size / 1024).toFixed(1)} KB`);
                            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Error al procesar la imagen'));
            };
            img.src = objectUrl;
        });
    };

    const getCurrentLocation = () => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => resolve({
                        latitude: position.coords.latitude.toString(),
                        longitude: position.coords.longitude.toString()
                    }),
                    (error) => reject(error),
                    // maximumAge 60s: usa lectura caché del GPS si tiene menos de 1 minuto
                    // Evita bloquear al inspector esperando GPS en el momento del submit
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
                );
            } else {
                reject(new Error('Geolocalizacion no soportada'));
            }
        });
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files).slice(0, 1);
        if (files.length === 0) return;
        const validFiles = files.filter(file => {
            // Validar que sea imagen
            if (!file.type.startsWith('image/')) {
                alert(`${file.name} no es una imagen válida`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        setCompressing(true);
        // Comprimir cada imagen válida antes de guardarla
        Promise.all(validFiles.map(file => compressImage(file).catch(err => {
            console.error(err);
            return file; // fallback al original
        }))).then(compressedFiles => {
            if (compressedFiles.length > 0) {
                // Revocar previas
                previews.forEach(url => URL.revokeObjectURL(url));
                const newPreviews = compressedFiles.map(f => URL.createObjectURL(f));
                setPreviews(newPreviews);
                setDecisionData(prev => ({
                    ...prev,
                    evidence_photos: compressedFiles.slice(0, 1)
                }));
            }
        }).finally(() => {
            setCompressing(false);
            e.target.value = '';
        });
    };

    const removeFile = (index) => {
        if (previews[index]) {
            URL.revokeObjectURL(previews[index]);
        }
        setPreviews(prev => prev.filter((_, i) => i !== index));
        setDecisionData(prev => ({
            ...prev,
            evidence_photos: prev.evidence_photos.filter((_, i) => i !== index)
        }));
    };

    const requestConfirmation = (type) => {
        if (decisionData.evidence_photos.length === 0) {
            alert('Debe agregar la foto de evidencia (obligatoria)');
            return;
        }

        if (type === 'reject') {
            if (!decisionData.inspector_comments.trim() || decisionData.inspector_comments.length < 10) {
                alert('Los comentarios son obligatorios para rechazar (mínimo 10 caracteres)');
                return;
            }
        }

        setConfirmationType(type);
        setShowConfirmation(true);
    };

    const handleConfirmedAction = async () => {
        try {
            setMakingDecision(true);
            setShowConfirmation(false);
            setUploadProgress(10);

            // Usar la ubicación ya obtenida en segundo plano si existe para evitar retraso de GPS en submit
            const location = currentPosition || await getCurrentLocation();
            setUploadProgress(30);

            const actionData = { ...decisionData, ...location };
            setUploadProgress(50);

            if (confirmationType === 'approve') {
                await onApprove(unrealizable.id, actionData);
            } else {
                await onReject(unrealizable.id, actionData);
            }

            setUploadProgress(100);
        } catch (error) {
            alert(formatInspectorError(error));
            setUploadProgress(0);
        } finally {
            setMakingDecision(false);
        }
    };

    const getDurationLabel = (duration) => {
        if (duration === -1) return 'INDEFINIDO';
        return `${duration} minutos`;
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '15px',
                padding: '30px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '2px solid #f0f0f0',
                    paddingBottom: '15px'
                }}>
                    <h2 style={{ margin: 0, color: '#333' }}>
                        Revision de Incidencia #{unrealizable.id}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#666'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '10px',
                    marginBottom: '20px'
                }}>
                    <h3 style={{ marginTop: 0, color: '#333' }}>Informacion del Tecnico</h3>
                    <p><strong>Tecnico:</strong> {unrealizable.technician?.full_name}</p>
                    <p><strong>Distrito:</strong> {unrealizable.technician?.distrito}</p>
                    <p><strong>Coordenadas:</strong> {unrealizable.latitude}, {unrealizable.longitude}</p>
                    <p><strong>Fecha:</strong> {new Date(unrealizable.submitted_at).toLocaleString()}</p>

                    <h3 style={{ marginTop: '20px', color: '#333' }}>Detalles de la Incidencia</h3>
                    <p><strong>Motivo:</strong> {unrealizable.reason}</p>
                    <p><strong>Comentarios:</strong> {unrealizable.comments}</p>

                    {unrealizable.evidence_photo_path && (
                        <div style={{ marginTop: '15px' }}>
                            <p><strong>Evidencia del técnico:</strong></p>
                            {(() => {
                                const baseUrl = getMediaBaseUrl();

                                const imageUrl = `${baseUrl}/static/uploads/${unrealizable.evidence_photo_path}`;

                                return (
                                    <img
                                        src={imageUrl}
                                        alt="Evidencia"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '200px',
                                            borderRadius: '8px',
                                            border: '2px solid #ddd',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setExpandedImage(imageUrl)}
                                        onError={(e) => (e.target.style.display = 'none')}
                                    />
                                );
                            })()}

                            {/* Modal para imagen expandida con soporte de botón Atrás del celular y tache X */}
                            <ImageViewerModal src={expandedImage} onClose={() => setExpandedImage(null)} />
                        </div>
                    )}
                </div>

                <div>
                    <h3 style={{ color: '#333', marginBottom: '20px' }}>Tu Evaluacion</h3>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Duracion Justificada
                        </label>
                        <select
                            value={decisionData.justification_duration}
                            onChange={(e) => setDecisionData(prev => ({
                                ...prev,
                                justification_duration: parseInt(e.target.value)
                            }))}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                        >

                            {unrealizable.available_durations
                                ?.filter((duration) => duration !== -1) // 🔥 Elimina la opción INDEFINIDO
                                ?.filter((duration) => unrealizable.reason !== 'zona_complicada' || duration <= 120)
                                .map((duration) => (
                                    <option key={duration} value={duration}>
                                        {`${duration} minutos`}
                                    </option>
                                ))}

                        </select>
                        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                            Las opciones varian segun la hora de envio del tecnico
                        </small>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Comentarios (Obligatorio para rechazo)
                        </label>
                        <textarea
                            value={decisionData.inspector_comments}
                            onChange={(e) => setDecisionData(prev => ({
                                ...prev,
                                inspector_comments: e.target.value
                            }))}
                            placeholder="Agrega tus comentarios sobre la evaluacion..."
                            rows="3"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '8px',
                                fontSize: '14px',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Foto de Evidencia (Obligatoria - 1)
                        </label>
                        {decisionData.evidence_photos.length === 0 && !compressing && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <label
                                    style={{
                                        flex: 1,
                                        padding: '16px 10px',
                                        border: '2px dashed #007bff',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: '#f0f8ff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <span style={{ fontSize: '24px' }}>📷</span>
                                    <span style={{ color: '#007bff', fontWeight: '600', fontSize: '13px' }}>
                                        Tomar Foto
                                    </span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                <label
                                    style={{
                                        flex: 1,
                                        padding: '16px 10px',
                                        border: '2px dashed #8b5cf6',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: '#f5f3ff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <span style={{ fontSize: '24px' }}>🖼️</span>
                                    <span style={{ color: '#7c3aed', fontWeight: '600', fontSize: '13px' }}>
                                        Galería
                                    </span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                        )}

                        {compressing && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '16px', background: '#f0f7ff', borderRadius: '8px', border: '2px dashed #007bff', gap: '8px', boxSizing: 'border-box' }}>
                                <span style={{ fontSize: '24px' }}>⏳</span>
                                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#007bff' }}>Procesando y comprimiendo foto...</span>
                            </div>
                        )}

                        {decisionData.evidence_photos.length > 0 && (
                            <div style={{ marginTop: '15px' }}>
                                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                                    Foto seleccionada ({decisionData.evidence_photos.length}/1)
                                </h4>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                    gap: '10px'
                                }}>
                                    {decisionData.evidence_photos.map((file, index) => (
                                        <div key={index} style={{
                                            position: 'relative',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            border: '2px solid #e1e5e9'
                                        }}>
                                            <img
                                                src={previews[index] || ''}
                                                alt={`Preview ${index + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setExpandedImage(previews[index])}
                                            />
                                            <button
                                                onClick={() => removeFile(index)}
                                                style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    background: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ×
                                            </button>
                                            <div style={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                background: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '4px',
                                                fontSize: '10px',
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {file.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Barra de progreso */}
                {makingDecision && (
                    <div style={{
                        marginTop: '20px',
                        marginBottom: '10px'
                    }}>
                        <div style={{
                            background: '#e9ecef',
                            borderRadius: '10px',
                            height: '8px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                background: '#007bff',
                                height: '100%',
                                width: `${uploadProgress}%`,
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                        <div style={{
                            textAlign: 'center',
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '5px'
                        }}>
                            {uploadProgress < 30 ? 'Obteniendo ubicación...' :
                                uploadProgress < 50 ? 'Preparando datos...' :
                                    uploadProgress < 100 ? 'Enviando...' : 'Completado'}
                        </div>
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: '24px',
                    paddingTop: '16px',
                    borderTop: '1px solid #e2e8f0',
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    <button
                        onClick={() => requestConfirmation('approve')}
                        disabled={makingDecision || decisionData.evidence_photos.length === 0}
                        style={{
                            width: '100%',
                            padding: '15px 20px',
                            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '14px',
                            fontSize: '15.5px',
                            fontWeight: '700',
                            boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                            cursor: (makingDecision || decisionData.evidence_photos.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (makingDecision || decisionData.evidence_photos.length === 0) ? 0.5 : 1,
                            boxSizing: 'border-box'
                        }}
                    >
                        ✓ Aprobar
                    </button>

                    <button
                        onClick={() => requestConfirmation('reject')}
                        disabled={makingDecision || !decisionData.inspector_comments.trim() || decisionData.evidence_photos.length === 0}
                        style={{
                            width: '100%',
                            padding: '15px 20px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '14px',
                            fontSize: '15.5px',
                            fontWeight: '700',
                            boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
                            cursor: (makingDecision || !decisionData.inspector_comments.trim() || decisionData.evidence_photos.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (makingDecision || !decisionData.inspector_comments.trim() || decisionData.evidence_photos.length === 0) ? 0.5 : 1,
                            boxSizing: 'border-box'
                        }}
                    >
                        ✕ Rechazar
                    </button>

                    <button
                        onClick={onClose}
                        disabled={makingDecision}
                        style={{
                            width: '100%',
                            padding: '14px 20px',
                            background: '#f1f5f9',
                            color: '#334155',
                            border: '1.5px solid #cbd5e1',
                            borderRadius: '14px',
                            fontSize: '15px',
                            fontWeight: '700',
                            cursor: makingDecision ? 'not-allowed' : 'pointer',
                            boxSizing: 'border-box'
                        }}
                    >
                        Cancelar
                    </button>
                </div>

                {/* Modal de confirmación */}
                {showConfirmation && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '400px',
                            width: '90%'
                        }}>
                            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>
                                {confirmationType === 'approve' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
                            </h3>
                            <p style={{ color: '#666', margin: '0 0 20px 0' }}>
                                {confirmationType === 'approve'
                                    ? `¿Estás seguro de aprobar esta incidencia con ${getDurationLabel(decisionData.justification_duration)} de justificación?`
                                    : '¿Estás seguro de rechazar esta incidencia? Esta acción no se puede deshacer.'
                                }
                            </p>
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={handleCloseConfirmation}
                                    style={{
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmedAction}
                                    style={{
                                        background: confirmationType === 'approve' ? '#28a745' : '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const InspectorPage = () => {
    const { user, logout } = useAuth();
    const { DevModeGuardComponent } = useDevModeGuard({ enabled: true, onLogout: logout });
    const [pendingUnrealizables, setPendingUnrealizables] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedUnrealizable, setSelectedUnrealizable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [pageLoadingMessage, setPageLoadingMessage] = useState('');

    const [activeTab, setActiveTabState] = useState(() => {
        return sessionStorage.getItem('inspector_active_tab') || 'pending';
    });
    const setActiveTab = (tabKey) => {
        setIsPageLoading(true);
        setPageLoadingMessage(
            tabKey === 'pending' ? 'Recuperando incidencias pendientes...' :
                tabKey === 'pi' ? 'Cargando módulo de Planta Interna...' :
                    'Cargando recorrido de Planta Externa...'
        );
        setActiveTabState(tabKey);
        sessionStorage.setItem('inspector_active_tab', tabKey);
        setTimeout(() => {
            setIsPageLoading(false);
        }, 400);
    };
    const [selectedDistrict, setSelectedDistrict] = useState('todos');
    const [selectedPriority, setSelectedPriority] = useState('todas');
    const [selectedDateFilter, setSelectedDateFilter] = useState('todas');
    const [selectedRegion, setSelectedRegion] = useState('todas');
    const [selectedWeek, setSelectedWeek] = useState(() => {
        const saved = localStorage.getItem('inspector_selected_week');
        return (saved && saved !== 'todas') ? saved : null;
    });
    const [showMapModal, setShowMapModal] = useState(false);
    const [selectedMapUnrealizable, setSelectedMapUnrealizable] = useState(null);

    // NUEVO: Estado para PI mapa
    const [showPiRouteMap, setShowPiRouteMap] = useState(false);

    // ========== WORKDAY (JORNADA) STATE ==========
    const [workdayState, setWorkdayState] = useState('loading'); // 'loading', 'initial', 'active'
    const [workdayId, setWorkdayId] = useState(null);
    const [workdayStartTime, setWorkdayStartTime] = useState(null);
    const [peSegments, setPeSegments] = useState([]);
    const [peSegmentCount, setPeSegmentCount] = useState(0);
    const [peTotalDistance, setPeTotalDistance] = useState(0);
    const [workdayStartLocation, setWorkdayStartLocation] = useState(null);
    const [showPEMap, setShowPEMap] = useState(false);
    const [startingWorkday, setStartingWorkday] = useState(false);
    const [endingWorkday, setEndingWorkday] = useState(false);

    const handleClosePEMap = useBackHandler(showPEMap, (updatedSegments) => {
        if (updatedSegments && Array.isArray(updatedSegments)) {
            setPeSegments(updatedSegments);
        }
        setShowPEMap(false);
    }, 'showPEMap');
    const handleCloseMapModal = useBackHandler(showMapModal, () => {
        setShowMapModal(false);
        setSelectedMapUnrealizable(null);
    }, 'showMapModal');
    const handleCloseUnrealizable = useBackHandler(!!selectedUnrealizable, () => {
        setSelectedUnrealizable(null);
    }, 'selectedUnrealizable');

    // ========== SIMULADOR STAGING STATE ==========
    const [simulatedLocation, setSimulatedLocation] = useState(null);
    const [simulatedTimeOffsetMinutes, setSimulatedTimeOffsetMinutes] = useState(0);
    const [simPanelPos, setSimPanelPos] = useState({ x: 10, y: null }); // null y = use bottom positioning
    const [isDraggingSimPanel, setIsDraggingSimPanel] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const simPanelRef = useRef(null);

    // El simulador solo se habilita en web staging o localhost de desarrollo, NUNCA en APK móvil ni en producción
    const isSimulatorEnabled = isSimulatorAllowed();

    const getSimulatedDate = () => {
        const d = new Date();
        if (isSimulatorEnabled && simulatedTimeOffsetMinutes !== 0) {
            d.setTime(d.getTime() + simulatedTimeOffsetMinutes * 60 * 1000);
        }
        return d;
    };

    const formatTimeOffset = (totalMinutes) => {
        if (totalMinutes === 0) return 'AHORA';
        const sign = totalMinutes > 0 ? '+' : '-';
        const abs = Math.abs(totalMinutes);
        const days = Math.floor(abs / 1440);
        const hours = Math.floor((abs % 1440) / 60);
        const mins = abs % 60;
        let parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0) parts.push(`${mins}m`);
        return `${sign}${parts.join(' ')}`;
    };

    const handleSimPanelDragStart = (e) => {
        if (!simPanelRef.current) return;
        const touch = e.touches ? e.touches[0] : e;
        const rect = simPanelRef.current.getBoundingClientRect();
        setDragOffset({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
        setIsDraggingSimPanel(true);
        e.preventDefault();
    };

    const handleSimPanelDrag = useCallback((e) => {
        if (!isDraggingSimPanel) return;
        const touch = e.touches ? e.touches[0] : e;
        const newX = Math.max(0, Math.min(window.innerWidth - 50, touch.clientX - dragOffset.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 50, touch.clientY - dragOffset.y));
        setSimPanelPos({ x: newX, y: newY });
        e.preventDefault();
    }, [isDraggingSimPanel, dragOffset]);

    const handleSimPanelDragEnd = useCallback(() => {
        setIsDraggingSimPanel(false);
    }, []);

    useEffect(() => {
        if (isDraggingSimPanel) {
            window.addEventListener('mousemove', handleSimPanelDrag);
            window.addEventListener('mouseup', handleSimPanelDragEnd);
            window.addEventListener('touchmove', handleSimPanelDrag, { passive: false });
            window.addEventListener('touchend', handleSimPanelDragEnd);
            return () => {
                window.removeEventListener('mousemove', handleSimPanelDrag);
                window.removeEventListener('mouseup', handleSimPanelDragEnd);
                window.removeEventListener('touchmove', handleSimPanelDrag);
                window.removeEventListener('touchend', handleSimPanelDragEnd);
            };
        }
    }, [isDraggingSimPanel, handleSimPanelDrag, handleSimPanelDragEnd]);

    const moveSimulatedLocation = (metersLat, metersLng) => {
        setSimulatedLocation(prev => {
            const start = prev || { latitude: 25.686667, longitude: -100.316667 };
            const newLat = start.latitude + (metersLat / 111111);
            const newLng = start.longitude + (metersLng / (111111 * Math.cos(start.latitude * Math.PI / 180)));

            const newLoc = {
                latitude: newLat,
                longitude: newLng,
                timestamp: new Date().toISOString()
            };
            showToast(`📍 Ubicación movida: ${newLat.toFixed(6)}, ${newLng.toFixed(6)}`, 'info');
            return newLoc;
        });
    };

    // Sincronizar ubicación simulador con GPS real inicial
    useEffect(() => {
        if (isSimulatorEnabled && !simulatedLocation && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setSimulatedLocation({
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                        timestamp: new Date().toISOString()
                    });
                },
                (err) => {
                    console.warn('Falla GPS inicial en simulador:', err);
                    setSimulatedLocation({
                        latitude: 25.686667,
                        longitude: -100.316667,
                        timestamp: new Date().toISOString()
                    });
                }
            );
        }
    }, [isSimulatorEnabled, simulatedLocation]);

    // Definir incidencias de alta prioridad
    const HIGH_PRIORITY_REASONS = ['zona_roja', 'zona_complicada', 'mal_clima', 'problemas_acceso'];

    // Recover active workday on mount
    useEffect(() => {
        const checkActiveWorkday = async () => {
            try {
                let wd = null;
                try {
                    const result = await inspectorService.getActiveWorkday();
                    if (result.success && result.has_active_workday) {
                        wd = result.workday;
                        localStorage.setItem('inspector_cached_active_workday', JSON.stringify(wd));
                    } else if (result.success && !result.has_active_workday) {
                        localStorage.removeItem('inspector_cached_active_workday');
                    }
                } catch (netErr) {
                    console.warn('⚠️ Sin conexión al consultar jornada activa, usando caché local...');
                    const cached = localStorage.getItem('inspector_cached_active_workday');
                    if (cached) {
                        try { wd = JSON.parse(cached); } catch (e) { }
                    }
                }

                if (wd) {
                    setWorkdayId(wd.id);
                    setWorkdayStartTime(wd.start_time);
                    if (wd.start_latitude && wd.start_longitude) {
                        setWorkdayStartLocation({
                            latitude: parseFloat(wd.start_latitude),
                            longitude: parseFloat(wd.start_longitude)
                        });
                    }
                    const serverSegs = wd.segments || [];
                    const offlineItems = await storageService.getPendingInspectorSegments(wd.id);
                    const offlineSegs = (offlineItems || []).map(item => ({
                        id: `offline_${item.id}`,
                        offlineId: item.id,
                        latitude: item.latitude,
                        longitude: item.longitude,
                        recorded_at: item.recorded_at,
                        distance_from_previous: item.distance_from_previous || 0,
                        network_type: item.network_type,
                        incidences: item.incidences || [],
                        photo_blob: item.photo_blob,
                        is_offline: true
                    }));
                    const combined = [...serverSegs, ...offlineSegs];
                    setPeSegments(combined);
                    setPeSegmentCount(combined.length);
                    const calcTotal = combined.reduce((sum, s) => sum + (s.distance_from_previous || 0), 0);
                    setPeTotalDistance(calcTotal);
                    setWorkdayState('active');
                } else {
                    setWorkdayState('initial');
                }
            } catch (err) {
                console.error('Error checking active workday:', err);
                const cached = localStorage.getItem('inspector_cached_active_workday');
                if (cached) {
                    try {
                        const wd = JSON.parse(cached);
                        setWorkdayId(wd.id);
                        setWorkdayStartTime(wd.start_time);
                        setWorkdayState('active');
                    } catch (e) {
                        setWorkdayState('initial');
                    }
                } else {
                    setWorkdayState('initial');
                }
            }
        };
        checkActiveWorkday();
        // Reporte silencioso de estado del dispositivo (solo APK nativa)
        // Fire-and-forget: no esperamos resultado ni mostramos nada al inspector
        reportDeviceStatus();
    }, []);

    const handleStartWorkday = async () => {
        setStartingWorkday(true);
        try {
            const pos = await new Promise((resolve) => {
                if (isSimulatorEnabled && simulatedLocation) {
                    resolve(simulatedLocation);
                    return;
                }
                navigator.geolocation.getCurrentPosition(
                    p => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
                    (err) => {
                        console.warn('⚠️ GPS timeout/error en startWorkday, usando fallback:', err);
                        resolve({ latitude: 23.6345, longitude: -102.5528 });
                    },
                    { enableHighAccuracy: true, timeout: 8000 }
                );
            });
            const simTime = (isSimulatorEnabled && simulatedTimeOffsetMinutes !== 0) ? getSimulatedDate().toISOString() : null;
            const result = await inspectorService.startWorkday(pos.latitude, pos.longitude, simTime);
            if (result.success) {
                const startLat = result.start_latitude || pos.latitude;
                const startLng = result.start_longitude || pos.longitude;
                const wdData = {
                    id: result.workday_id,
                    start_time: result.start_time,
                    start_latitude: startLat,
                    start_longitude: startLng,
                    segments: result.segments || []
                };
                localStorage.setItem('inspector_cached_active_workday', JSON.stringify(wdData));
                setWorkdayId(result.workday_id);
                setWorkdayStartTime(result.start_time);
                if (startLat && startLng) {
                    setWorkdayStartLocation({
                        latitude: parseFloat(startLat),
                        longitude: parseFloat(startLng)
                    });
                }
                setPeSegments(result.segments || []);
                setPeSegmentCount(result.segment_count || 0);
                setPeTotalDistance(result.total_distance_meters || 0);
                setWorkdayState('active');
                showToast(result.existing_workday ? 'Jornada existente recuperada' : 'Jornada iniciada correctamente', 'success');
            }
        } catch (err) {
            showToast('Error al iniciar jornada: ' + err.message, 'error');
        } finally {
            setStartingWorkday(false);
        }
    };

    const handleEndWorkday = async () => {
        if (workdayId) {
            const pendingOffline = await storageService.getPendingInspectorSegments(workdayId);
            const hasOfflineInState = peSegments.some(s => s.is_offline);
            if ((pendingOffline && pendingOffline.length > 0) || hasOfflineInState) {
                showToast('⚠️ No puedes finalizar el recorrido/jornada mientras tengas segmentos guardados offline pendientes de envío. Por favor busca una zona con buena señal y presiona "Enviar" en tus segmentos pendientes.', 'warning');
                return;
            }
        }

        if (!window.confirm('¿Estás seguro de finalizar tu jornada? Esta acción no se puede deshacer.')) return;
        setEndingWorkday(true);
        try {
            const pos = await new Promise((resolve) => {
                if (isSimulatorEnabled && simulatedLocation) {
                    resolve(simulatedLocation);
                    return;
                }
                navigator.geolocation.getCurrentPosition(
                    p => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
                    (err) => {
                        console.warn('⚠️ GPS timeout/error en endWorkday, usando fallback:', err);
                        resolve({ latitude: 23.6345, longitude: -102.5528 });
                    },
                    { enableHighAccuracy: true, timeout: 8000 }
                );
            });
            const simTime = (isSimulatorEnabled && simulatedTimeOffsetMinutes !== 0) ? getSimulatedDate().toISOString() : null;
            const result = await inspectorService.endWorkday(workdayId, pos.latitude, pos.longitude, simTime);
            if (result.success) {
                localStorage.removeItem('inspector_cached_active_workday');
                if (workdayId) {
                    await storageService.clearInspectorSegments(workdayId);
                }
                showToast(`Jornada finalizada. Tiempo: ${result.total_time_minutes} min, Puntos PE / PI: ${result.segment_count}`, 'success');
                setWorkdayState('initial');
                setWorkdayId(null);
                setWorkdayStartTime(null);
                setPeSegments([]);
                setPeSegmentCount(0);
                setPeTotalDistance(0);
            }
        } catch (err) {
            showToast('Error al finalizar jornada: ' + err.message, 'error');
        } finally {
            setEndingWorkday(false);
        }
    };

    const handlePESegmentAdded = (totals, newSegment) => {
        setPeSegmentCount(totals.segment_count);
        setPeTotalDistance(totals.total_distance_meters);
        if (newSegment) {
            setPeSegments(prev => [...prev, newSegment]);
        }
    };

    // ========== AUTO-SYNC DE COLA OFFLINE ==========
    // Sincroniza automáticamente los segmentos PE e incidencias/validaciones PI guardadas
    // en IndexedDB cuando el inspector recupera señal después de estar offline
    const offlineSync = useOfflineSync({
        workdayId,
        onSegmentSynced: (syncedSeg) => {
            // Cuando un segmento offline se syncó con éxito, actualizar UI
            setPeSegments(prev => prev.map(s =>
                s.offlineId === syncedSeg.id ? { ...s, is_offline: false } : s
            ));
            showToast('✅ Segmento offline sincronizado', 'success');
        },
        onActionSynced: (action) => {
            // Cuando una incidencia/validación se sincronizó, recargar datos
            showToast(`✅ Enviado: ${action.label}`, 'success');
            if (action.type === 'approve_unrealizable' || action.type === 'reject_unrealizable') {
                loadPendingUnrealizables();
            }
        }
    });
    const { isOnline, pendingCount: offlinePendingCount, pendingActions: offlinePendingActions, isSyncing: isSyncingOffline } = offlineSync;

    // Mostrar toasts cuando cambia el estado de conectividad
    const prevOnlineRef = useRef(null);
    useEffect(() => {
        if (prevOnlineRef.current === null) {
            prevOnlineRef.current = isOnline;
            return;
        }
        if (!isOnline && prevOnlineRef.current) {
            showToast('\u26a1 Sin señal — los segmentos se guardarán offline', 'warning');
        } else if (isOnline && !prevOnlineRef.current) {
            showToast('\U0001f4f6 Señal recuperada — sincronizando datos...', 'info');
        }
        prevOnlineRef.current = isOnline;
    }, [isOnline]); // eslint-disable-line

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    // Verificar si una incidencia es de alta prioridad
    const isHighPriority = (reason) => {
        return HIGH_PRIORITY_REASONS.includes(reason);
    };

    // Filtrar por fecha
    const filterByDate = (unrealizable) => {
        if (selectedDateFilter === 'todas') return true;

        const submittedDate = new Date(unrealizable.submitted_at);
        const now = new Date();
        const diffDays = Math.floor((now - submittedDate) / (1000 * 60 * 60 * 24));

        switch (selectedDateFilter) {
            case 'hoy':
                return diffDays === 0;
            case 'ultimos_3':
                return diffDays <= 3;
            case 'semana':
                return diffDays <= 7;
            default:
                return true;
        }
    };

    // Obtener lista única de distritos con contadores
    const getDistrictStats = () => {
        const stats = {};
        pendingUnrealizables.forEach(item => {
            const distrito = item.technician?.distrito || 'Sin distrito';
            stats[distrito] = (stats[distrito] || 0) + 1;
        });
        return stats;
    };

    // Obtener información de la semana ISO de la fecha de envío del segmento
    const getIsoWeekInfo = useCallback((dateString) => {
        if (!dateString) return { weekNumber: 0, year: 0, key: 'sin_fecha', label: 'Sin fecha' };
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return { weekNumber: 0, year: 0, key: 'sin_fecha', label: 'Sin fecha' };

        const target = new Date(d.valueOf());
        const dayNr = (d.getDay() + 6) % 7;
        target.setDate(target.getDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setMonth(0, 1);
        if (target.getDay() !== 4) {
            target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
        }
        const weekNumber = 1 + Math.ceil((firstThursday - target) / 604800000);
        const year = target.getFullYear();
        const key = `W${weekNumber}-${year}`;
        return { weekNumber, year, key, label: `Semana ${weekNumber}` };
    }, []);

    // Generar ÚNICAMENTE las 3 semanas ISO más recientes (Semana Actual, Semana Anterior, Hace 2 Semanas)
    const get3RecentWeeks = useCallback(() => {
        const results = [];
        const now = new Date();
        for (let i = 0; i < 3; i++) {
            const d = new Date(now);
            d.setDate(now.getDate() - (i * 7));
            const info = getIsoWeekInfo(d.toISOString());
            if (!results.some(r => r.key === info.key)) {
                results.push(info);
            }
        }
        return results;
    }, [getIsoWeekInfo]);

    const recentWeeksList = get3RecentWeeks();
    const allowedWeekKeys = new Set(recentWeeksList.map(w => w.key));

    // Auto-seleccionar la semana más reciente si no hay ninguna seleccionada o es inválida
    useEffect(() => {
        if (recentWeeksList.length > 0) {
            const defaultWeekKey = recentWeeksList[0].key;
            if (!selectedWeek || !allowedWeekKeys.has(selectedWeek)) {
                setSelectedWeek(defaultWeekKey);
            }
        }
    }, [recentWeeksList, selectedWeek]); // eslint-disable-line

    // Filtrar incidencias por todos los criterios (restringiendo a las 3 semanas más recientes)
    const filteredUnrealizables = pendingUnrealizables
        .filter(item => {
            // Filtrar estrictamente solo las últimas 3 semanas
            const info = getIsoWeekInfo(item.submitted_at);
            if (!allowedWeekKeys.has(info.key)) return false;

            // Filtro por región (NUEVO - PRIMERO)
            if (selectedRegion !== 'todas') {
                const region = item.report_info?.region || item.technician?.region || 'Sin región';
                if (region !== selectedRegion) return false;
            }

            // Filtro por distrito
            if (selectedDistrict !== 'todos') {
                const distrito = item.technician?.distrito || 'Sin distrito';
                if (distrito !== selectedDistrict) return false;
            }

            // Filtro por prioridad
            if (selectedPriority !== 'todas') {
                const isPriority = isHighPriority(item.reason);
                if (selectedPriority === 'alta' && !isPriority) return false;
                if (selectedPriority === 'normal' && isPriority) return false;
            }

            // Filtro por fecha
            return filterByDate(item);
        });

    // Obtener las 3 semanas con su conteo de incidencias pendientes
    const getWeekStats = () => {
        const stats = {};
        recentWeeksList.forEach(w => {
            stats[w.key] = {
                key: w.key,
                label: w.label,
                weekNumber: w.weekNumber,
                year: w.year,
                count: 0
            };
        });

        filteredUnrealizables.forEach(item => {
            const info = getIsoWeekInfo(item.submitted_at);
            if (stats[info.key]) {
                stats[info.key].count += 1;
            }
        });

        return Object.values(stats);
    };

    const handleWeekSelect = (weekKey) => {
        setIsPageLoading(true);
        setPageLoadingMessage('Recuperando y filtrando incidencias de la semana seleccionada...');
        setSelectedWeek(weekKey);
        localStorage.setItem('inspector_selected_week', weekKey);
        setTimeout(() => {
            setIsPageLoading(false);
        }, 350);
    };

    // Incidencias filtradas a desplegar según la semana elegida (Optimización de DOM / RAM)
    const displayedUnrealizables = selectedWeek ? filteredUnrealizables.filter(item => {
        const info = getIsoWeekInfo(item.submitted_at);
        return info.key === selectedWeek;
    }) : [];

    const loadPendingUnrealizables = useCallback(async () => {
        try {
            setLoading(true);
            const response = await inspectorService.getPendingUnrealizables();
            if (response.success) {
                const list = response.unrealizables || [];
                setPendingUnrealizables(list);
                localStorage.setItem('inspector_cached_pending_unrealizables', JSON.stringify(list));

                // Precargar fotos de evidencia del técnico en la caché del navegador para verlas sin señal
                try {
                    const baseUrl = getMediaBaseUrl();
                    list.forEach(u => {
                        if (u.evidence_photo_path) {
                            const img = new Image();
                            img.src = `${baseUrl}/static/uploads/${u.evidence_photo_path}`;
                        }
                    });
                } catch (e) {
                    console.warn('Error precargando fotos de evidencia:', e);
                }
            }
        } catch (error) {
            console.warn('⚠️ Sin conexión al cargar incidencias, cargando desde caché local...');
            const cached = localStorage.getItem('inspector_cached_pending_unrealizables');
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    setPendingUnrealizables(parsed || []);
                } catch (e) {}
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await inspectorService.getMyApprovals();
            if (response.success) {
                setHistory(response.approvals || []);
            }
        } catch (error) {
            console.warn('⚠️ Sin conexión al cargar historial');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPendingUnrealizables();
    }, [loadPendingUnrealizables]);

    const handleViewDetails = async (unrealizableId) => {
        try {
            const response = await inspectorService.getUnrealizableDetail(unrealizableId);
            if (response.success) {
                setSelectedUnrealizable(response.unrealizable);
                return;
            }
        } catch (error) {
            console.warn('⚠️ Sin conexión al cargar detalle, usando datos locales...');
        }
        // Fallback local
        const found = pendingUnrealizables.find(u => u.id === unrealizableId);
        if (found) {
            setSelectedUnrealizable(found);
        } else {
            showToast('No se encontró el detalle de la incidencia', 'error');
        }
    };

    // Helper: detecta si un error es de red (no de lógica de negocio)
    const isNetworkError = (err) => {
        if (!err) return false;
        const msg = err.message || '';
        return (
            !navigator.onLine ||
            err.name === 'AbortError' ||
            err.name === 'TypeError' ||
            msg.includes('Failed to fetch') ||
            msg.includes('NetworkError') ||
            msg.includes('señal de red') ||
            msg.includes('Sin respuesta del servidor')
        );
    };

    const handleApprove = async (unrealizableId, data) => {
        try {
            const response = await inspectorService.approveUnrealizable(unrealizableId, data);
            if (response.success) {
                showToast('✅ Incidencia aprobada correctamente', 'success');
                setSelectedUnrealizable(null);
                setPendingUnrealizables(prev => {
                    const updated = prev.filter(u => u.id !== unrealizableId);
                    localStorage.setItem('inspector_cached_pending_unrealizables', JSON.stringify(updated));
                    return updated;
                });
                if (navigator.onLine) loadPendingUnrealizables();
            }
        } catch (error) {
            if (isNetworkError(error)) {
                // Sin señal: guardar en cola offline para envío automático
                try {
                    await storageService.queueInspectorAction({
                        type: 'approve_unrealizable',
                        endpoint: `${API_BASE_URL}/inspector/unrealizable/${unrealizableId}/approve`,
                        method: 'POST',
                        fields: {
                            inspector_lat: data.latitude || data.inspector_lat,
                            inspector_lng: data.longitude || data.inspector_lng,
                            justification_duration: String(data.justification_duration),
                            inspector_comments: data.inspector_comments || ''
                        },
                        photos: data.evidence_photos || [],
                        photoFieldName: 'evidence_photos',
                        label: `Aprobación incidencia #${unrealizableId}`
                    });
                    showToast('📴 Sin señal — aprobación guardada. Se enviará automáticamente cuando recuperes señal.', 'warning');
                    setSelectedUnrealizable(null);
                    // Remover visualmente la incidencia aprobada y actualizar caché local
                    setPendingUnrealizables(prev => {
                        const updated = prev.filter(u => u.id !== unrealizableId);
                        localStorage.setItem('inspector_cached_pending_unrealizables', JSON.stringify(updated));
                        return updated;
                    });
                    offlineSync.refreshPendingCount();
                } catch (queueErr) {
                    showToast('Error al guardar offline: ' + queueErr.message, 'error');
                }
            } else {
                showToast(error.message || 'Error aprobando la incidencia', 'error');
            }
        }
    };

    const handleReject = async (unrealizableId, data) => {
        try {
            const response = await inspectorService.rejectUnrealizable(unrealizableId, data);
            if (response.success) {
                showToast('✅ Incidencia rechazada correctamente', 'success');
                setSelectedUnrealizable(null);
                setPendingUnrealizables(prev => {
                    const updated = prev.filter(u => u.id !== unrealizableId);
                    localStorage.setItem('inspector_cached_pending_unrealizables', JSON.stringify(updated));
                    return updated;
                });
                if (navigator.onLine) loadPendingUnrealizables();
            }
        } catch (error) {
            if (isNetworkError(error)) {
                // Sin señal: guardar en cola offline
                try {
                    await storageService.queueInspectorAction({
                        type: 'reject_unrealizable',
                        endpoint: `${API_BASE_URL}/inspector/unrealizable/${unrealizableId}/reject`,
                        method: 'POST',
                        fields: {
                            inspector_lat: data.latitude || data.inspector_lat,
                            inspector_lng: data.longitude || data.inspector_lng,
                            inspector_comments: data.inspector_comments || ''
                        },
                        photos: data.evidence_photos || [],
                        photoFieldName: 'evidence_photos',
                        label: `Rechazo incidencia #${unrealizableId}`
                    });
                    showToast('📴 Sin señal — rechazo guardado. Se enviará automáticamente cuando recuperes señal.', 'warning');
                    setSelectedUnrealizable(null);
                    // Remover visualmente la incidencia rechazada y actualizar caché local
                    setPendingUnrealizables(prev => {
                        const updated = prev.filter(u => u.id !== unrealizableId);
                        localStorage.setItem('inspector_cached_pending_unrealizables', JSON.stringify(updated));
                        return updated;
                    });
                    offlineSync.refreshPendingCount();
                } catch (queueErr) {
                    showToast('Error al guardar offline: ' + queueErr.message, 'error');
                }
            } else {
                showToast(error.message || 'Error rechazando la incidencia', 'error');
            }
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            approved: { background: '#28a745', color: 'white' },
            rejected: { background: '#dc3545', color: 'white' }
        };
        const labels = {
            approved: 'Aprobado',
            rejected: 'Rechazado'
        };
        return (
            <span style={{
                ...styles[status],
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
            }}>
                {labels[status]}
            </span>
        );
    };
    // Obtener lista única de regiones con contadores
    const getRegionStats = () => {
        const stats = {};
        pendingUnrealizables.forEach(item => {
            // La región viene del reporte, NO del técnico
            const region = item.report_info?.region || item.technician?.region || 'Sin región';
            stats[region] = (stats[region] || 0) + 1;
        });
        return stats;
    };


    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
            {/* 🛡️ Alerta y bloqueo reactivo de Modo Desarrollador */}
            <DevModeGuardComponent />
            <header style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <h1 style={{ margin: '0 0 5px 0', fontSize: '1.5rem' }}>Panel de Inspector</h1>
                        <p style={{ margin: 0 }}>Bienvenido, {user?.full_name}</p>
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>Region: {user?.region}</p>
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>VERSION 2.3.0</p>
                    </div>
                    <button
                        onClick={logout}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            border: '2px solid white',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600'
                        }}
                    >
                        Cerrar Sesion
                    </button>
                </div>
            </header>

            {workdayState === 'loading' ? (
                <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px', textAlign: 'center' }}>
                    <div style={{ color: 'white', fontSize: '1.2rem' }}>⏳ Cargando estado de jornada...</div>
                </main>
            ) : workdayState === 'initial' ? (
                <main style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
                    <div style={{
                        background: 'white', borderRadius: 20, padding: '40px 30px',
                        boxShadow: '0 15px 40px rgba(0,0,0,0.25)', textAlign: 'center'
                    }}>
                        <div style={{ fontSize: 50, marginBottom: 10 }}>📍</div>
                        <h2 style={{ margin: '0 0 10px', color: '#333', fontSize: '1.5rem' }}>Iniciar Recorrido de Inspección</h2>
                        <p style={{ color: '#888', fontSize: 14, margin: '0 0 8px' }}>
                            ⚠️ NO OLVIDES FINALIZAR TU RECORRIDO AL FINALIZAR TU JORNADA LABORAL ⚠️
                        </p>
                        <hr style={{ border: 'none', borderTop: '2px solid #667eea', margin: '15px auto', width: '60%' }} />
                        <p style={{ color: '#666', fontSize: 13, marginBottom: 25 }}>
                            Se capturarán tus coordenadas y hora de inicio.<br />
                            Dentro del recorrido podrás: registrar puntos de red aéreos/canalizados y reportar incidencias de PE y PI.
                        </p>
                        <button
                            onClick={handleStartWorkday}
                            disabled={startingWorkday}
                            style={{
                                background: startingWorkday ? '#6c757d' : 'linear-gradient(135deg, #667eea, #764ba2)',
                                color: '#fff', border: 'none', padding: '14px 32px', borderRadius: 30,
                                fontSize: 16, fontWeight: 700, cursor: startingWorkday ? 'not-allowed' : 'pointer',
                                boxShadow: '0 4px 15px rgba(102,126,234,0.4)', transition: 'all 0.3s'
                            }}
                        >
                            {startingWorkday ? '⏳ Iniciando...' : '🚀 Iniciar Recorrido'}
                        </button>
                    </div>
                </main>
            ) : (
                <>
                    <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '10px' }}>
                        {/* Workday info bar */}
                        <div style={{
                            background: 'rgba(15, 23, 42, 0.75)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            borderRadius: 16,
                            padding: '12px 18px',
                            marginBottom: 16,
                            display: 'flex',
                            justify: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 10,
                            color: '#f8fafc',
                            fontSize: 13,
                            fontWeight: 600,
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                            boxShadow: '0 8px 25px rgba(15, 23, 42, 0.25)'
                        }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ color: '#38bdf8' }}>🕐</span> Inicio: {workdayStartTime ? new Date(workdayStartTime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ color: '#818cf8' }}>📍</span> Puntos PE / PI: <strong style={{ color: '#ffffff' }}>{peSegmentCount}</strong></span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ color: '#34d399' }}>📏</span> Metraje: <strong style={{ color: '#ffffff' }}>{peTotalDistance >= 1000 ? `${(peTotalDistance / 1000).toFixed(2)} km` : `${peTotalDistance} m`}</strong></span>
                            {/* Indicador de señal y sincronización offline */}
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                background: isOnline ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.22)',
                                border: `1px solid ${isOnline ? '#22c55e' : '#ef4444'}`,
                                borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700,
                                color: isOnline ? '#86efac' : '#fca5a5',
                                boxShadow: isOnline ? '0 0 12px rgba(34,197,94,0.25)' : '0 0 12px rgba(239,68,68,0.25)'
                            }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? '#22c55e' : '#ef4444', display: 'inline-block', boxShadow: isOnline ? '0 0 6px #22c55e' : '0 0 6px #ef4444' }}></span>
                                {isSyncingOffline
                                    ? '🔄 Sincronizando...'
                                    : isOnline
                                        ? (offlinePendingCount > 0 ? `📶 Señal OK · ${offlinePendingCount} pendientes` : '📶 Señal OK')
                                        : `📴 Sin señal${offlinePendingCount > 0 ? ` · ${offlinePendingCount} en cola` : ''}`}
                            </span>
                        </div>

                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                            {[
                                { key: 'pi', label: (process.env.REACT_APP_ENV === 'staging' || process.env.REACT_APP_ENABLE_PE_VERIFICATIONS === 'true') ? '✅ Verificaciones PI / PE' : '✅ Verificaciones PI' },
                                { key: 'pending', label: `📋 Justificaciones (${pendingUnrealizables.length})` },
                                { key: 'pe_route', label: '🗺️ Recorrido PE / PI' },
                            ].map(tab => (
                                <button key={tab.key} onClick={() => tab.key === 'pe_route' ? setShowPEMap(true) : setActiveTab(tab.key)}
                                    style={{
                                        padding: '12px 22px', border: (activeTab === tab.key && tab.key !== 'pe_route') ? 'none' : '1.5px solid #cbd5e1',
                                        borderRadius: 14, fontWeight: 700,
                                        fontSize: 14, cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                                        background: (activeTab === tab.key && tab.key !== 'pe_route') ? 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)' : '#ffffff',
                                        color: (activeTab === tab.key && tab.key !== 'pe_route') ? '#ffffff' : '#334155',
                                        boxShadow: (activeTab === tab.key && tab.key !== 'pe_route') ? '0 4px 14px rgba(37,99,235,0.35)' : '0 2px 6px rgba(0,0,0,0.03)',
                                    }}
                                >{tab.label}</button>
                            ))}
                        </div>

                        {/* Content card */}
                        <div style={{
                            background: '#ffffff', borderRadius: 20, padding: 20,
                            boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.06)', width: '100%',
                            border: '1px solid #e2e8f0',
                            boxSizing: 'border-box', overflow: 'hidden'
                        }}>
                            <div style={{ display: activeTab === 'pi' ? 'block' : 'none' }}>
                                <InspectorDashboardTab poliza="PI" />
                            </div>

                            <div style={{ display: activeTab === 'pending' ? 'block' : 'none' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 15 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <h2 style={{ margin: 0 }}>Justificaciones</h2>
                                        <button
                                            onClick={loadPendingUnrealizables}
                                            disabled={loading}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '18px',
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                transition: 'background 0.2s',
                                                opacity: loading ? 0.5 : 1
                                            }}
                                            title="Actualizar lista"
                                        >
                                            🔄
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                        <select value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)}
                                            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', background: 'white' }}>
                                            <option value="todos">Todos los distritos</option>
                                            {Object.entries(getDistrictStats()).map(([d, c]) => <option key={d} value={d}>{d} ({c})</option>)}
                                        </select>
                                        <select value={selectedPriority} onChange={e => setSelectedPriority(e.target.value)}
                                            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', background: 'white' }}>
                                            <option value="todas">Todas las prioridades</option>
                                            <option value="alta">Prioridad Alta</option>
                                            <option value="normal">Prioridad Normal</option>
                                        </select>
                                    </div>
                                </div>

                                {loading ? (
                                    <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Cargando justificaciones...</div>
                                ) : filteredUnrealizables.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                                        {pendingUnrealizables.length === 0 ? 'No hay incidencias pendientes' : `No hay incidencias en ${selectedDistrict}`}
                                    </div>
                                ) : (
                                    <>
                                        {/* Selector de Semanas con permanencia (Optimización de RAM y DOM) */}
                                        <div style={{
                                            marginBottom: 15,
                                            background: '#f8fafc',
                                            padding: 12,
                                            borderRadius: 12,
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            <div style={{
                                                fontSize: 13,
                                                fontWeight: 700,
                                                color: '#475569',
                                                marginBottom: 8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}>
                                                <span>📅 SELECCIONAR SEMANA (ÚLTIMAS 3 SEMANAS)</span>
                                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                                    Últimas 3 semanas ({filteredUnrealizables.length} incidencias)
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                {getWeekStats().map(w => (
                                                    <button
                                                        key={w.key}
                                                        onClick={() => handleWeekSelect(w.key)}
                                                        style={{
                                                            padding: '8px 16px', borderRadius: 20, border: 'none',
                                                            fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                                            background: selectedWeek === w.key ? '#059669' : '#e2e8f0',
                                                            color: selectedWeek === w.key ? '#fff' : '#475569',
                                                            boxShadow: selectedWeek === w.key ? '0 3px 8px rgba(5,150,105,0.3)' : 'none',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        {w.label} ({w.count})
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {!selectedWeek ? (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '40px 20px',
                                                color: '#475569',
                                                background: '#f8fafc',
                                                borderRadius: 14,
                                                border: '2px dashed #cbd5e1',
                                                margin: '20px 0'
                                            }}>
                                                <div style={{ fontSize: 42, marginBottom: 10 }}>👆</div>
                                                <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Selecciona una semana arriba</div>
                                                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                                                    Haz clic en cualquier semana para desplegar sus incidencias pendientes.
                                                </div>
                                            </div>
                                        ) : displayedUnrealizables.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: 30, color: '#666' }}>
                                                No hay incidencias pendientes en la semana seleccionada
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 15 }}>
                                                {displayedUnrealizables.map(u => (
                                                    <div key={u.id} style={{
                                                        border: '1.5px solid ' + (isHighPriority(u.reason) ? '#fca5a5' : '#e2e8f0'),
                                                        borderLeft: '5px solid ' + (isHighPriority(u.reason) ? '#ef4444' : '#2563eb'),
                                                        borderRadius: 16, padding: 18, position: 'relative',
                                                        background: isHighPriority(u.reason) ? 'linear-gradient(135deg, #ffffff 0%, #fff5f5 100%)' : '#ffffff',
                                                        boxShadow: '0 4px 18px rgba(0,0,0,0.04)', transition: 'all 0.2s ease'
                                                    }}>
                                                        {isHighPriority(u.reason) && (
                                                            <div style={{ position: 'absolute', top: 14, right: 14, background: '#ef4444', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, boxShadow: '0 2px 8px rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }}></span> PRIORIDAD ALTA
                                                            </div>
                                                        )}
                                                        <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Incidencia #{u.id}</h3>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 12px', fontSize: 13, color: '#475569', marginBottom: 14 }}>
                                                            <div><strong style={{ color: '#1e293b' }}>👤 Técnico:</strong> {u.technician?.full_name || 'N/A'}</div>
                                                            <div><strong style={{ color: '#1e293b' }}>📍 Distrito:</strong> {u.technician?.distrito || 'N/A'}</div>
                                                            <div><strong style={{ color: '#1e293b' }}>⚠️ Motivo:</strong> {u.reason}</div>
                                                            <div><strong style={{ color: '#1e293b' }}>📅 Fecha:</strong> {new Date(u.submitted_at).toLocaleString('es-MX')}</div>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                                                            <button onClick={() => handleViewDetails(u.id)} style={{ flex: 1, padding: '11px 18px', border: 'none', borderRadius: 12, background: 'linear-gradient(135deg, #1d4ed8, #2563eb)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13.5, boxShadow: '0 4px 12px rgba(37,99,235,0.25)', transition: 'all 0.2s' }}>📋 Ver Detalles</button>
                                                            <button onClick={() => { setSelectedMapUnrealizable(u); setShowMapModal(true); }} style={{ flex: 1, padding: '11px 18px', border: 'none', borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13.5, boxShadow: '0 4px 12px rgba(16,185,129,0.25)', transition: 'all 0.2s' }}>🗺️ Ver Mapa</button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Finalizar Jornada button */}
                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <button onClick={handleEndWorkday} disabled={endingWorkday}
                                style={{
                                    background: endingWorkday ? '#6c757d' : 'linear-gradient(135deg, #dc3545, #c82333)',
                                    color: '#fff', border: 'none', padding: '14px 32px', borderRadius: 30,
                                    fontSize: 16, fontWeight: 700, cursor: endingWorkday ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 15px rgba(220,53,69,0.4)'
                                }}>
                                {endingWorkday ? '⏳ Finalizando...' : '🏁 Finalizar Jornada'}
                            </button>
                        </div>
                    </main>

                    {/* PE Map Modal */}
                    {showPEMap && workdayId && (
                        <InspectorPEMapModal
                            workdayId={workdayId}
                            initialSegments={peSegments}
                            onClose={handleClosePEMap}
                            onSegmentAdded={handlePESegmentAdded}
                            simulatedLocation={simulatedLocation}
                            simulatedTimeOffsetMinutes={simulatedTimeOffsetMinutes}
                            startLocation={workdayStartLocation}
                        />
                    )}
                </>
            )}

            {selectedUnrealizable && (
                <UnrealizableDetailModal
                    unrealizable={selectedUnrealizable}
                    onClose={handleCloseUnrealizable}
                    onApprove={handleApprove}
                    onReject={handleReject}
                />
            )}

            {showMapModal && selectedMapUnrealizable && (
                <MapModal
                    unrealizable={selectedMapUnrealizable}
                    onClose={handleCloseMapModal}
                />
            )}

            {/* 🛠️ PANEL SIMULADOR DRAGGABLE (SOLO STAGING/DEV) */}
            {isSimulatorEnabled && (
                <div
                    ref={simPanelRef}
                    style={{
                        position: 'fixed',
                        ...(simPanelPos.y !== null
                            ? { top: `${simPanelPos.y}px`, left: `${simPanelPos.x}px` }
                            : { bottom: '80px', left: `${simPanelPos.x}px` }
                        ),
                        width: 'min(340px, calc(100vw - 20px))',
                        background: 'rgba(0,0,0,0.9)',
                        color: 'white',
                        borderRadius: '12px',
                        zIndex: 4000,
                        border: '2px solid #ffc107',
                        boxShadow: '0 0 20px rgba(0,0,0,0.5)',
                        userSelect: 'none',
                        touchAction: 'none'
                    }}
                >
                    {/* DRAG HANDLE */}
                    <div
                        onMouseDown={handleSimPanelDragStart}
                        onTouchStart={handleSimPanelDragStart}
                        style={{
                            padding: '8px 12px',
                            cursor: 'grab',
                            background: 'rgba(255,193,7,0.25)',
                            borderRadius: '10px 10px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(255,255,255,0.15)'
                        }}
                    >
                        <span style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>🛠️ SIMULADOR STAGING</span>
                        <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>⠿ arrastra</span>
                    </div>

                    <div style={{ padding: '10px 12px' }}>
                        {/* ─── GPS SECTION ─── */}
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ display: 'flex', justifycontent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '0.8rem' }}>📍 GPS</span>
                                <span style={{ fontSize: '0.65rem', opacity: 0.7, marginLeft: '10px' }}>
                                    {simulatedLocation ? `${simulatedLocation.latitude.toFixed(5)}, ${simulatedLocation.longitude.toFixed(5)}` : 'Cargando...'}
                                </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px' }}>
                                <button
                                    onClick={() => moveSimulatedLocation(60, 0)}
                                    style={{ padding: '6px', background: '#28a745', border: 'none', borderRadius: '5px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}
                                >
                                    🏃 +60m
                                </button>
                                <button
                                    onClick={() => moveSimulatedLocation(250, 0)}
                                    style={{ padding: '6px', background: '#dc3545', border: 'none', borderRadius: '5px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}
                                >
                                    🚗 +250m
                                </button>
                                <button
                                    onClick={() => setSimulatedLocation(null)}
                                    style={{ padding: '6px', background: '#6c757d', border: 'none', borderRadius: '5px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}
                                >
                                    🔄 Reset
                                </button>
                            </div>
                        </div>

                        {/* ─── TIEMPO SECTION ─── */}
                        <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                            <div style={{ display: 'flex', justifycontent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '0.8rem' }}>⏱️ TIEMPO</span>
                                <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: simulatedTimeOffsetMinutes !== 0 ? '#ffc107' : '#8f8',
                                    background: simulatedTimeOffsetMinutes !== 0 ? 'rgba(255,193,7,0.2)' : 'rgba(0,255,0,0.1)',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    marginLeft: '10px'
                                }}>
                                    {formatTimeOffset(simulatedTimeOffsetMinutes)}
                                </span>
                            </div>
                            <div style={{ fontSize: '0.65rem', marginBottom: '6px', opacity: 0.7, textAlign: 'center' }}>
                                Hora simulada: {getSimulatedDate().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })} — {getSimulatedDate().toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </div>

                            {/* Media hora buttons */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginBottom: '5px' }}>
                                <button
                                    onClick={() => setSimulatedTimeOffsetMinutes(prev => prev - 30)}
                                    style={{ padding: '6px', background: '#0d6efd', border: 'none', borderRadius: '5px', color: 'white', fontSize: '0.72rem', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    ◀ -30 min
                                </button>
                                <button
                                    onClick={() => setSimulatedTimeOffsetMinutes(prev => prev + 30)}
                                    style={{ padding: '6px', background: '#0d6efd', border: 'none', borderRadius: '5px', color: 'white', fontSize: '0.72rem', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    +30 min ▶
                                </button>
                            </div>

                            {/* Day buttons */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginBottom: '5px' }}>
                                <button
                                    onClick={() => setSimulatedTimeOffsetMinutes(prev => prev - 1440)}
                                    style={{ padding: '5px', background: '#343a40', border: '1px solid #6c757d', borderRadius: '4px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}
                                >
                                    ⏪ -1 Día
                                </button>
                                <button
                                    onClick={() => setSimulatedTimeOffsetMinutes(prev => prev + 1440)}
                                    style={{ padding: '5px', background: '#343a40', border: '1px solid #6c757d', borderRadius: '4px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}
                                >
                                    +1 Día ⏩
                                </button>
                            </div>

                            {/* Reset */}
                            <button
                                onClick={() => setSimulatedTimeOffsetMinutes(0)}
                                disabled={simulatedTimeOffsetMinutes === 0}
                                style={{
                                    width: '100%',
                                    padding: '5px',
                                    background: simulatedTimeOffsetMinutes !== 0 ? '#dc3545' : '#6c757d',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: simulatedTimeOffsetMinutes !== 0 ? 'white' : '#aaa',
                                    fontSize: '0.7rem',
                                    cursor: simulatedTimeOffsetMinutes !== 0 ? 'pointer' : 'default'
                                }}
                            >
                                🔄 Reset Tiempo (Ahora)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: '' })}
            />

            <InspectorLoadingOverlay
                active={isPageLoading || loading || startingWorkday || endingWorkday || workdayState === 'loading'}
                message={
                    workdayState === 'loading' ? "Cargando estado de la jornada..." :
                        startingWorkday ? "Iniciando jornada y sincronizando posición GPS..." :
                            endingWorkday ? "Finalizando jornada y guardando métricas..." :
                                pageLoadingMessage || "Consultando servidor y actualizando datos..."
                }
                onCancel={() => {
                    setIsPageLoading(false);
                    setLoading(false);
                    setStartingWorkday(false);
                    setEndingWorkday(false);
                    if (workdayState === 'loading') setWorkdayState('initial');
                    showToast('Sincronización cancelada por el usuario', 'info');
                }}
            />
        </div>
    );
};

export default InspectorPage;